import { useAppStore } from '@/dashboard/store/appStore';
import { useProjectAddAndEdit } from '@/dashboard/store/projectAddEditPopupStore';
import { storeToRefs } from 'pinia';
import { computed, ComputedRef, ref, Ref, watch } from 'vue';
import { CurrentUser } from '@/dashboard/types/current_user';
import { Step, StepType } from '@/dashboard/types/step';
import type { ProjectVisibility } from '@/dashboard/types/project_visibility';
import TeamUser from '@/dashboard/types/teamUser';
import { Project } from '@/dashboard/types/project';
import { getProjectVisibilityType, isAllowPublic } from '@/dashboard/types/project_visibility';
import { UserRole, UserRoleType } from '@/dashboard/types/user_role';
import { OwnerAdminRoles } from '@/dashboard/constants/roles';

export function useCreateProjectPopup() {
  const appStore = useAppStore();
  const projectAddAndEdit = useProjectAddAndEdit();

  const { isCreate, showPopup, project, step, workspace, tabs } = storeToRefs(projectAddAndEdit);
  const currentUser: ComputedRef<CurrentUser | undefined> = computed(() => {
    return workspace.value?.user;
  });
  const buttonText: ComputedRef<string> = computed(() => {
    if (!isCreate.value) {
      return 'Submit';
    } else if (isCreate.value && step.value === Step.Name && type.value !== 'private') {
      return 'Next';
    } else {
      return 'Create';
    }
  });
  const showTeamMenu: ComputedRef<boolean> = computed(() => type.value === 'team' || type.value === 'link');
  const showPrivate: ComputedRef<boolean> = computed(() => team.value.length <= 1);
  const canEditBase: ComputedRef<boolean> = computed(() => {
    if (isCreate.value) {
      return isCreate.value;
    } else {
      const role = currentUser.value?.role;
      return !!role && OwnerAdminRoles.includes(role);
    }
  });

  const type: Ref<ProjectVisibility> = ref('team');
  const name: Ref<string> = ref('');
  const nameError: Ref<boolean> = ref(false);
  const team: Ref<TeamUser[]> = ref([]);

  function closePopup() {
    projectAddAndEdit.closePopup();
  }

  async function submitAction() {
    if (!validateName()) {
      return;
    }

    let responseProject: Project | null | undefined = undefined;

    if (isCreate.value) {
      if (step.value === Step.Name && type.value !== 'private') {
        projectAddAndEdit.setStep(Step.Team);
        return;
      }

      responseProject = await projectAddAndEdit.create(name.value, isAllowPublic(type.value), team.value, type.value);
    } else if (project.value && !isCreate.value) {
      responseProject = await projectAddAndEdit.edit(name.value, isAllowPublic(type.value), team.value, type.value);
    }

    if (responseProject) {
      setTimeout(() => closePopup(), 10);
      if (responseProject.id === project.value?.id) {
        appStore.setProject(responseProject);
      }
      await appStore.loadWorkspaces();
    }
  }

  function validateName() {
    const text = name.value.trim();

    if (text.length <= 0) {
      nameError.value = true;
      step.value = Step.Name;
      return false;
    } else {
      nameError.value = false;
      return true;
    }
  }

  function onTeamUpdated(updatedTeam: TeamUser[]) {
    team.value = updatedTeam;
  }

  function setStep(step: StepType) {
    projectAddAndEdit.setStep(step);
  }

  watch(showPopup, () => {
    const currentProject = project.value;

    if (showPopup.value && !!currentProject && !!currentProject.users) {
      name.value = currentProject.name;
      type.value = getProjectVisibilityType(currentProject);
      team.value = currentProject.users.map((item) => {
        return new TeamUser(item.email, item.role, 'project', item.image, true);
      });
    } else if (showPopup.value) {
      if (workspace.value) {
        team.value = workspace.value.users
          .map((item) => new TeamUser(item.email, item.role, 'project', item.image, false))
          .sort((a, b) => {
            if (a.isOwner() && !b.isOwner()) {
              return -1;
            } else if (!a.isOwner() && b.isOwner()) {
              return 1;
            } else {
              return 0;
            }
          });
      }

      name.value = 'New directory';
      type.value = 'team';
    }
  });

  return {
    canEditBase,
    type,
    isCreate,
    currentUser,
    buttonText,
    step,
    workspace,
    project,
    showPopup,
    name,
    nameError,
    showTeamMenu,
    showPrivate,
    team,
    tabs,
    onTeamUpdated,
    submitAction,
    closePopup,
    setStep,
  };
}
